/**
 * Wishlist hook - auth-aware with Firebase sync
 */
import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from './useTypedRedux';
import {
  addToWishlist,
  removeFromWishlist,
  clearWishlist,
  selectWishlistItems,
  selectWishlistCount,
  selectWishlistLoading,
} from '@/store/slices/wishlistSlice';
import { selectIsAuthenticated, selectUser } from '@/store/slices/authSlice';
import { openLoginPrompt } from '@/store/slices/uiSlice';
import {
  addToWishlistFB,
  removeFromWishlistFB,
  clearWishlistFB,
} from '@/services/firebase/wishlist.service';
import { Product } from '@/types';

export const useWishlist = () => {
  const dispatch = useAppDispatch();

  const items = useAppSelector(selectWishlistItems);
  const count = useAppSelector(selectWishlistCount);
  const isLoading = useAppSelector(selectWishlistLoading);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);

  const add = useCallback(
    (product: Product) => {
      if (!isAuthenticated || !user) {
        dispatch(openLoginPrompt());
        return;
      }
      dispatch(addToWishlist(product));
      addToWishlistFB(user.id, product.id);
    },
    [dispatch, isAuthenticated, user]
  );

  const remove = useCallback(
    (productId: string) => {
      dispatch(removeFromWishlist(productId));
      if (isAuthenticated && user) {
        removeFromWishlistFB(user.id, productId);
      }
    },
    [dispatch, isAuthenticated, user]
  );

  const toggle = useCallback(
    (product: Product) => {
      if (!isAuthenticated || !user) {
        dispatch(openLoginPrompt());
        return;
      }
      const inList = items.some((item) => item.product.id === product.id);
      if (inList) {
        dispatch(removeFromWishlist(product.id));
        removeFromWishlistFB(user.id, product.id);
      } else {
        dispatch(addToWishlist(product));
        addToWishlistFB(user.id, product.id);
      }
    },
    [dispatch, isAuthenticated, user, items]
  );

  const clear = useCallback(() => {
    dispatch(clearWishlist());
    if (isAuthenticated && user) {
      clearWishlistFB(user.id);
    }
  }, [dispatch, isAuthenticated, user]);

  const isInWishlist = useCallback(
    (productId: string) => {
      return items.some((item) => item.product.id === productId);
    },
    [items]
  );

  return {
    items,
    count,
    isEmpty: items.length === 0,
    isLoading,
    add,
    remove,
    toggle,
    clear,
    isInWishlist,
  };
};

export default useWishlist;
